import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import SEO from "@/components/SEO";
import { products, ProductCategory } from "@/data/products";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, ChevronRight } from "lucide-react";
import styleModern from "@/assets/style-modern.jpg";
import styleAdm from "@/assets/style-adm.jpg";
import styleCondensed from "@/assets/style-condensed.jpg";
import productChangeArm from "@/assets/product-change-arm.png";
import productStorageCabinet from "@/assets/product-storage-cabinet.png";
import productStorageCase from "@/assets/product-storage-case.png";

const STYLE_IMAGES: Record<string, string> = {
  Modern: styleModern,
  ADM: styleAdm,
  Condensed: styleCondensed,
};

type Filter = "all" | ProductCategory;

const getMinPrice = (product: (typeof products)[0]): number | null => {
  if (product.sizes && product.sizes.length > 0) {
    return Math.min(...product.sizes.map((s) => s.price));
  }
  if (product.variants && product.variants.length > 0) {
    return Math.min(...product.variants.map((v) => v.price));
  }
  if (product.price > 0) return product.price;
  return null;
};

const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function Shop() {
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get("category") as Filter | null;
  const [filter, setFilter] = useState<Filter>(initialCat || "letter-set");

  const filtered = useMemo(() => {
    if (filter === "all") return products;
    return products.filter((p) => p.category === filter);
  }, [filter]);

  const accessories = useMemo(() => products.filter((p) => p.category === "accessory"), []);

  return (
    <Layout>
      <SEO
        title="Shop Changeable Letter Sets & Accessories"
        description="Browse Gemini Pronto™ changeable letter sets in Modern, ADM, and Condensed styles. Plus Pronto Track, change arms, storage cases, and rivets."
        canonical="/shop"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Shop Pronto Changeable Letters",
          description: "Gemini Pronto™ changeable letter sets and accessories for signs.",
          url: "https://pronto-letters.com/shop",
        }}
      />
      <div className="container py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold">Shop</h1>
            <p className="mt-2 text-muted-foreground">Browse Pronto™ changeable letter sets and accessories.</p>
          </div>
          <a
            href="/docs/Pronto-Sell-Sheet.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
          >
            <Download className="h-4 w-4 text-accent" />
            Product Sell Sheet
          </a>
        </div>

        {/* Filters */}
        <div className="mt-8 flex gap-2">
          {(["letter-set", "accessory"] as Filter[]).map((f) => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
            >
              {f === "letter-set" ? "Letter Sets" : "Accessories"}
            </Button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((product) => {
            const minPrice = getMinPrice(product);
            return (
              <Link key={product.id} to={`/shop/${product.slug}`}>
                <Card className="group h-full overflow-hidden transition-shadow hover:shadow-lg">
                  <div className="flex h-48 items-center justify-center overflow-hidden bg-white p-4">
                    <img
                      src={product.style && STYLE_IMAGES[product.style] ? STYLE_IMAGES[product.style] : product.image}
                      alt={product.name}
                      className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-5">
                    {product.style && (
                      <span className="text-xs font-medium uppercase tracking-wider text-accent">
                        {product.style}
                      </span>
                    )}
                    <h3 className="mt-1 font-display text-lg font-semibold">{product.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{product.description}</p>
                    <p className="mt-3 font-semibold">
                      {minPrice ? `From ${formatPrice(minPrice)}` : "Contact for pricing"}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Accessories Spotlight — only shown when viewing Letter Sets */}
        {filter === "letter-set" && (
          <div className="mt-20">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-accent">Complete Your Setup</p>
                <h2 className="mt-1 font-display text-2xl font-bold">Accessories</h2>
                <p className="mt-1 text-sm text-muted-foreground">Everything you need to install, change, and store your Pronto letters.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setFilter("accessory")} className="shrink-0 gap-1">
                View All Accessories <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {accessories.map((acc) => {
                const minPrice = getMinPrice(acc);
                return (
                  <Link key={acc.id} to={`/shop/${acc.slug}`}>
                    <Card className="group overflow-hidden transition-shadow hover:shadow-md">
                      <div className="flex h-36 items-center justify-center overflow-hidden bg-white p-3">
                        <img
                          src={acc.image}
                          alt={acc.name}
                          className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-display text-sm font-semibold leading-snug">{acc.name}</h3>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{acc.description}</p>
                        <p className="mt-2 text-sm font-semibold">
                          {minPrice ? `From ${formatPrice(minPrice)}` : "Contact for pricing"}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
